import { getRegisterUrlClientSide } from '@/app/utils/urlHelper';
import { useAuth0User } from '@/modules/Context/useAuth0User';
import { tryToNoticeClientError } from '@/utils/clientErrorInfo';
import { useGoogleAnalytics } from '@/utils/useGoogleAnalytics';
import { addFavoriteJob, deleteFavoriteJob } from '@/utils/useRequest';
import cx from 'classnames';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useAnalytics } from 'utils/useAnalytics';

import { useMediaQuery } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import { useTheme } from '@material-ui/core/styles';
import CheckIcon from '@material-ui/icons/Check';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';

import styles from './style.module.scss';

interface JobFavouriteButtonProps {
  id: string;
  jobNumber: string;
  style?: string | null;
  favourited?: boolean;
  showText?: boolean;
  className?: string;
}

const JobFavouriteButton = ({
  id,
  jobNumber,
  style,
  favourited = false,
  showText = true,
  className,
}: JobFavouriteButtonProps) => {
  const track = useAnalytics();
  const trackGA = useGoogleAnalytics();
  const router = useRouter();
  const { user } = useAuth0User();
  const [favourite, setFavourite] = useState<boolean>(favourited);
  const [error, setError] = useState<string>();
  const [showButtonText, setShowButtonText] = useState<boolean>(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const isSaveAfterLogin = () => {
    return !!user && router?.query.saveJobId && id === router.query.saveJobId;
  };

  const handleSaveJobIdQueryParam = () => {
    const query = { ...router.query };
    delete query.saveJobId;
    router.push({ pathname: router.pathname, query: query });
  };

  const getRedirectUrlForSaveJob = () =>
    router.asPath.indexOf('?') !== -1
      ? `${router.asPath}&saveJobId=${id}`
      : `${router.asPath}?saveJobId=${id}`;

  const toggleFavourite = async () => {
    trackGA('click_save_job', {});
    if (!user) {
      router.push(getRegisterUrlClientSide('save_job', getRedirectUrlForSaveJob()));
      // there is a little blip in the UI where the favourited job is toggled before redirect. This prevents that.
      return;
    }
    try {
      if (favourite) {
        await deleteFavoriteJob(id);
      } else {
        await addFavoriteJob(id);
        track('Job Saved', { jobNumber: jobNumber });
      }
      setFavourite(!favourite);
    } catch (error) {
      tryToNoticeClientError(error);
      setError(error.message);
    }
  };

  useEffect(() => {
    // Only need to addFavouriteJob as you cannot unsave from a non-loggedin state
    const addFavourite = async () => {
      if (isSaveAfterLogin() && !favourite) {
        try {
          await addFavoriteJob(router.query.saveJobId);
          setFavourite(true);
          handleSaveJobIdQueryParam();
        } catch (error) {
          tryToNoticeClientError(error);
          setError(error.message);
        }
      } else if (isSaveAfterLogin() && favourite) {
        handleSaveJobIdQueryParam();
      }
    };
    addFavourite();
  }, [isSaveAfterLogin, favourite]);

  useEffect(() => {
    if (isMobile || !showText) {
      setShowButtonText(false);
    } else {
      setShowButtonText(true);
    }
  }, [isMobile, showText]);

  const isOutlined = style === 'outlined';
  return (
    <>
      {isOutlined && (
        <Button
          className={cx(styles.button, className)}
          fullWidth
          variant="outlined"
          size="large"
          color="secondary"
          onClick={toggleFavourite}
          data-testid="job-favourite-button"
          startIcon={favourite && <CheckIcon />}
        >
          {favourite ? 'Saved' : 'Save'}
        </Button>
      )}
      {!isOutlined && favourite && (
        <Button
          endIcon={<StarIcon />}
          className={styles.saveButton}
          color="primary"
          data-testid="job-favourite-button"
          onClick={toggleFavourite}
        >
          {showButtonText ? 'JOB SAVED' : null}
        </Button>
      )}
      {!isOutlined && !favourite && (
        <Button
          endIcon={<StarBorderIcon />}
          className={styles.saveButton}
          color="primary"
          data-testid="job-favourite-button"
          onClick={toggleFavourite}
        >
          {showButtonText ? 'SAVE JOB' : null}
        </Button>
      )}
    </>
  );
};

export default JobFavouriteButton;
